import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useUser } from '../../contexts/UserContext';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { api } from '../../utils/apiClient';
import { getCountryNameFromCode, isServiceAvailable } from '../../constants/countries';

/**
 * StripeWithdrawal component that allows users to manage token withdrawals
 * 
 * This page is only accessible to authenticated users.
 */
function StripeWithdrawal() {
  const { t, i18n } = useTranslation(['withdrawals']);
  const { user } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [dashboardLoading, setDashboardLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState('');
  const [payoutsEnabled, setPayoutsEnabled] = useState(false);
  const [transferCapabilityStatus, setTransferCapabilityStatus] = useState('');
  const [onboardingCompleted, setOnboardingCompleted] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState<number>(0);
  
  // Set document title
  useDocumentTitle(t('withdrawals.title', 'Token to Cash'));
  
  // Check if the selected country supports Stripe
  const isStripeSupported = user.countryCode 
    ? isServiceAvailable(user.countryCode, 'STRIPE')
    : false;
  
  // Exchange rate constants
  const EXCHANGE_RATE = 10000; // 10000 tokens = 100 USD
  const MINIMUM_WITHDRAWAL = 10000;
  
  // Handle withdraw amount changes
  const handleWithdrawAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    const amount = parseInt(value, 10) || 0;
    
    // Cap at available balance
    const maxAmount = user.tokenBalance || 0;
    const finalAmount = Math.min(amount, maxAmount);
    
    setWithdrawAmount(finalAmount);
  };
  
  // Set initial withdraw amount to available balance if above minimum
  useEffect(() => {
    if (user.tokenBalance && user.tokenBalance >= MINIMUM_WITHDRAWAL) {
      setWithdrawAmount(user.tokenBalance);
    } else {
      setWithdrawAmount(0);
    }
  }, [user.tokenBalance]);
  
  // Check if user has a country code set, if not redirect to withdrawal-setup
  useEffect(() => {
    if (user && !user.countryCode) {
      console.log('No country code found, redirecting to withdrawal setup');
      navigate('/withdrawal-setup');
    } else if (user && user.countryCode && !isStripeSupported) {
      // If user has a country but it doesn't support Stripe, redirect back to withdrawal setup
      console.log('Country does not support Stripe, redirecting to withdrawal setup');
      navigate('/withdrawal-setup');
    }
  }, [user, user.countryCode, navigate, isStripeSupported]);
  
  // Create a ref to track if initial check is done
  const initialCheckRef = React.useRef(false);
  
  // Create a separate ref to track URL parameter processing
  const urlParamsProcessedRef = React.useRef(false);
  
  // Only check for Stripe connection when explicitly requested (not automatic)
  const checkStripeConnection = useCallback(async () => {
    if (!user.email) return;
    
    try {
      setLoading(true);
      
      const response = await api.wallet.getStripeAccountStatus(user.email);
      
      // Set states based on the response
      setStripeConnected(!!(response.success && response.stripeConnected));
      setStripeAccountId(response.accountId || response.stripeAccountId || '');
      setOnboardingCompleted(!!response.detailsSubmitted);
      setPayoutsEnabled(!!response.payoutsEnabled);
      setTransferCapabilityStatus(response.transfer_capability_status || 'inactive');
      
      setLoading(false);
    } catch (error) {
      console.error('Error checking Stripe connection:', error);
      setLoading(false);
      setError('Failed to check Stripe connection status. Please try again.');
    }
  }, [user.email]);
  
  // One-time check on mount
  useEffect(() => {
    // Only run once when component mounts
    if (!initialCheckRef.current) {
      initialCheckRef.current = true;
      
      // Check if we have URL parameters that need processing
      const params = new URLSearchParams(location.search);
      const hasRefreshParam = params.get('refresh') === 'true';
      const hasSuccessParam = params.get('success') === 'true';
      const hasRedirectParam = params.get('redirect') === 'true';
      
      if ((hasRefreshParam || hasSuccessParam || hasRedirectParam) && !urlParamsProcessedRef.current) {
        urlParamsProcessedRef.current = true;
        
        // Clean up URL parameters immediately to prevent loops
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Show success message if needed
        if (hasSuccessParam) {
          setSuccessMessage(t('withdrawals.stripeConnectSuccess', 'Your Stripe account has been connected successfully.'));
        }
        
        // Show special message for redirects from incomplete onboarding
        if (hasRedirectParam) {
          setSuccessMessage(t('withdrawals.stripeRedirectInfo', 'You can resume your Stripe account setup from where you left off.'));
        }
      }
      
      // Make the initial stripe connection check if user is logged in
      if (user.email) {
        checkStripeConnection();
      }
    }
  }, [t, user.email, location.search, checkStripeConnection]);
  
  // Handle connecting to Stripe
  const handleConnectStripe = async () => {
    if (!user.email || !user.countryCode) {
      // If no country code set, redirect to withdrawal setup
      navigate('/withdrawal-setup');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      
      // Add redirect=true parameter to help handle incomplete onboarding redirects
      const returnUrl = window.location.origin + window.location.pathname + '?redirect=true';
      
      const response = await api.wallet.createStripeAccount(
        user.email, 
        user.countryCode,
        returnUrl
      );
      
      if (response.success && response.url) {
        // Redirect to Stripe Connect onboarding
        window.location.href = response.url;
      } else {
        setError(response.message || t('withdrawals.stripeConnectionFailed', 'Failed to connect with Stripe. Please try again.'));
      }
    } catch (err) {
      console.error('Error connecting to Stripe:', err);
      setError(t('withdrawals.stripeConnectionFailed', 'Failed to connect with Stripe. Please try again.'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle setting percentage of available balance
  const handleSetPercentage = (percentage: number) => {
    const amount = Math.floor((user.tokenBalance * percentage) / 100);
    setWithdrawAmount(amount);
  };
  
  // Handle input blur (validate minimum)
  const handleInputBlur = () => {
    if (withdrawAmount > 0 && withdrawAmount < MINIMUM_WITHDRAWAL) {
      setError(t('withdrawals.minimumRequiredError', 'The minimum withdrawal amount is {{minimum}} tokens.', { minimum: MINIMUM_WITHDRAWAL.toLocaleString() }));
    } else {
      setError('');
    }
  };
  
  // Handle opening the Stripe dashboard
  const handleOpenDashboard = async () => {
    if (!user.email || !stripeConnected) return;
    
    try {
      setDashboardLoading(true);
      setError('');
      
      const response = await api.wallet.createDashboardLink(user.email);
      
      if (response.success && response.url) {
        // Open the Stripe dashboard in a new tab
        window.open(response.url, '_blank');
      } else {
        setError(response.message || t('withdrawals.dashboardLinkFailed', 'Failed to create dashboard link. Please try again.'));
      }
    } catch (err) {
      console.error('Error creating dashboard link:', err);
      setError(t('withdrawals.dashboardLinkFailed', 'Failed to create dashboard link. Please try again.'));
    } finally {
      setDashboardLoading(false);
    }
  };
  
  // Handle exchanging tokens for credit
  const handleWithdraw = async () => {
    if (!user.email || !stripeConnected || !payoutsEnabled || withdrawAmount < MINIMUM_WITHDRAWAL) return;
    
    try {
      setLoading(true);
      setError('');
      setSuccessMessage('');
      
      // Note: The API function name remains the same for backward compatibility, but UI text is updated
      const response = await api.wallet.burnTokensForCredit(user.email, withdrawAmount);
      
      if (response.success) {
        // Calculate the USD amount based on the exchange rate
        const usdAmount = ((withdrawAmount / EXCHANGE_RATE) * 100).toFixed(2);
        
        // Show detailed message with amount in USD
        setSuccessMessage(
          t(
            'withdrawals.withdrawalInitiatedWithAmount', 
            '{{tokens}} tokens have been exchanged and {{amount}} {{currency}} has been credited to your Stripe account successfully.', 
            { 
              tokens: response.tokens || withdrawAmount, 
              amount: response.amount || usdAmount, 
              currency: 'USD' 
            }
          )
        );
        
        // Update user's token balance in UI
        user.tokenBalance = user.tokenBalance - withdrawAmount;
        
        // Reset withdraw amount if balance is below minimum
        if (user.tokenBalance < MINIMUM_WITHDRAWAL) {
          setWithdrawAmount(0);
        } else {
          setWithdrawAmount(user.tokenBalance);
        }
      } else {
        setError(response.message || t('withdrawals.withdrawalFailed', 'Failed to exchange tokens for credit. Please try again.'));
      }
    } catch (err) {
      console.error('Error exchanging tokens for credit:', err);
      setError(t('withdrawals.withdrawalFailed', 'Failed to exchange tokens for credit. Please try again.'));
    } finally {
      setLoading(false);
    }
  };
  
  // Handle resuming Stripe onboarding
  const handleResumeOnboarding = useCallback(async () => {
    if (!user.email || !stripeAccountId) return;
    
    try {
      setLoading(true);
      setError('');
      
      // Add redirect=true parameter to help handle incomplete onboarding redirects
      const returnUrl = window.location.origin + window.location.pathname + '?redirect=true';
      
      // We can use the existing createStripeAccount function - the server will
      // look up the user's Stripe account by email, no need to pass country
      const response = await api.wallet.createStripeAccount(
        user.email,
        user.countryCode as string, // Ensure it's treated as a string
        returnUrl
      );
      
      if (response.success && response.url) {
        // Redirect to Stripe Connect onboarding
        window.location.href = response.url;
      } else {
        setError(response.message || t('withdrawals.stripeConnectionFailed', 'Failed to resume onboarding with Stripe. Please try again.'));
      }
    } catch (err) {
      console.error('Error resuming Stripe onboarding:', err);
      setError(t('withdrawals.stripeConnectionFailed', 'Failed to resume onboarding with Stripe. Please try again.'));
    } finally {
      setLoading(false);
    }
  }, [user.email, user.countryCode, stripeAccountId, t]);
  
  // Get capability status display
  const getCapabilityStatusDisplay = () => {
    switch (transferCapabilityStatus) {
      case 'inactive':
        return (
          <span className="badge bg-secondary">
            <i className="bi bi-x-circle me-1"></i>
            {t('withdrawals.transferInactive', 'Transfer Inactive')}
          </span>
        );
      case 'pending':
        return (
          <span className="badge bg-warning">
            <i className="bi bi-hourglass-split me-1"></i>
            {t('withdrawals.transferPending', 'Transfer Pending')}
          </span>
        );
      case 'active':
        return (
          <span className="badge bg-success">
            <i className="bi bi-check-circle me-1"></i>
            {t('withdrawals.transferActive', 'Transfer Active')}
          </span>
        );
      case 'unrequested':
        return (
          <span className="badge bg-info">
            <i className="bi bi-question-circle me-1"></i>
            {t('withdrawals.transferUnrequested', 'Transfer Not Requested')}
          </span>
        );
      default:
        return (
          <span className="badge bg-secondary">
            <i className="bi bi-dash-circle me-1"></i>
            {t('withdrawals.transferUnknown', 'Unknown')}
          </span>
        );
    }
  };
  
  return (
    <div className="container py-5">
      {/* Header */}
      <div className="row mb-5">
        <div className="col-12">
          <div className="d-flex align-items-center mb-3">
            <Link to="/wallet" className="btn btn-outline-secondary me-3">
              <i className="bi bi-arrow-left"></i>
            </Link>
            <h3 className="fw-bold mb-0">{t('withdrawals.title', 'Token to Cash')}</h3>
          </div>
          <p className="text-muted">{t('withdrawals.description', 'Convert your tokens into real money through your Stripe account')}</p>
        </div>
      </div>
      
      {/* Status Messages */}
      {error && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-danger d-flex align-items-center" role="alert">
              <i className="bi bi-exclamation-circle-fill me-2"></i>
              <div>{error}</div>
            </div>
          </div>
        </div>
      )}
      
      {successMessage && (
        <div className="row mb-4">
          <div className="col-12">
            <div className="alert alert-success d-flex align-items-center" role="alert">
              <i className="bi bi-check-circle-fill me-2"></i>
              <div>{successMessage}</div>
            </div>
          </div>
        </div>
      )}
      
      {/* Main Content */}
      <div className="row g-4">
        <div className="col-lg-8">
          {/* Balance Card */}
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-body border-bottom-0 py-4">
              <h2 className="h4 mb-0">{t('withdrawals.available', 'Available Balance')}</h2>
            </div>
            <div className="card-body">
              <div className="d-flex align-items-center mb-4">
                <span className="fw-medium me-2">{t('withdrawals.currentBalance', 'Current Balance:')}</span>
                <span className={`badge ${user.tokenBalance < MINIMUM_WITHDRAWAL ? 'bg-warning' : 'bg-primary'} rounded-pill fs-5 px-3 py-2`}>
                  {user.tokenBalance.toLocaleString()}
                </span>
                {user.tokenBalance < MINIMUM_WITHDRAWAL && (
                  <span className="ms-2 text-danger">
                    <i className="bi bi-exclamation-circle me-1"></i>
                    {t('withdrawals.minimumRequired', 'Minimum required: {{minimum}} tokens', {minimum: MINIMUM_WITHDRAWAL.toLocaleString()})}
                  </span>
                )}
              </div>
              
              {!stripeConnected ? (
                <>  
                  <div className="alert alert-info border-0">
                    <div className="d-flex align-items-center mb-3">
                      <i className="bi bi-info-circle-fill fs-4 me-2"></i>
                      <h5 className="alert-heading mb-0">{t('withdrawals.connectWithStripe', 'Connect with Stripe to withdraw tokens')}</h5>
                    </div>
                    <p className="mb-3">{t('withdrawals.stripeConnectInfo', 'To withdraw your tokens, you need to connect your account with Stripe. This allows us to securely transfer funds to your bank account.')}</p>
                    <button 
                      className="btn btn-primary btn-lg"
                      onClick={handleConnectStripe}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          {t('withdrawals.connecting', 'Connecting...')}
                        </>
                      ) : (
                        <>
                          <i className="bi bi-stripe me-2"></i>
                          {t('withdrawals.connectWithStripe', 'Connect with Stripe')}
                        </>
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <div className="mb-4">
                  {stripeConnected && (
                    <div className="d-flex align-items-start">
                      <i className="bi bi-stripe fs-4 me-2 text-primary"></i>
                      <div>
                        <div className="fw-medium">{t('withdrawals.stripeConnected', 'Your account is connected with Stripe')}</div>
                        <div className="mt-3">
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">{t('withdrawals.stripeAccountId', 'Stripe Account ID:')}</span>
                            <span className="font-monospace">{stripeAccountId}</span>
                          </div>
  
                          {user.countryCode && (
                            <div className="d-flex justify-content-between mb-2">
                              <span className="text-muted">{t('withdrawals.country', 'Country:')}</span>
                              <span>
                                {t(`countries.${getCountryNameFromCode(user.countryCode)}`, getCountryNameFromCode(user.countryCode))}
                              </span>
                            </div>
                          )}
                          
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">{t('withdrawals.accountStatus', 'Account Status:')}</span>
                            <span>
                              {onboardingCompleted ? (
                                <span className="badge bg-success">
                                  <i className="bi bi-check-circle me-1"></i>
                                  Active
                                </span>
                              ) : (
                                <span className="badge bg-warning">
                                  <i className="bi bi-exclamation-circle me-1"></i>
                                  {t('withdrawals.onboardingNotCompleted', 'Onboarding Not Completed')}
                                </span>
                              )}
                            </span>
                          </div>
                          
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">{t('withdrawals.payoutsStatus', 'Payouts Status:')}</span>
                            <span>{payoutsEnabled ? (
                              <span className="badge bg-success">{t('withdrawals.payoutsEnabled', 'Payouts Enabled')}</span>
                            ) : (
                              <span className="badge bg-warning">{t('withdrawals.completeOnboarding', 'Complete Onboarding to Enable')}</span>
                            )}</span>
                          </div>
                          
                          <div className="d-flex justify-content-between mb-2">
                            <span className="text-muted">{t('withdrawals.transferCapability', 'Transfer Capability:')}</span>
                            <span>{getCapabilityStatusDisplay()}</span>
                          </div>
                        </div>
                        
                        <div className="mt-3">
                          {onboardingCompleted && (
                            <button
                              type="button"
                              className="btn btn-primary me-2"
                              onClick={handleOpenDashboard}
                              disabled={dashboardLoading}
                            >
                              {dashboardLoading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  {t('common.processing', 'Processing...')}
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-box-arrow-up-right me-2"></i>
                                  {t('withdrawals.accessDashboard', 'Access Stripe Dashboard')}
                                </>
                              )}
                            </button>
                          )}
                          
                          {!onboardingCompleted && (
                            <button
                              type="button"
                              className="btn btn-primary me-2"
                              onClick={handleResumeOnboarding}
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                  {t('common.processing', 'Processing...')}
                                </>
                              ) : (
                                <>
                                  <i className="bi bi-arrow-right-circle me-2"></i>
                                  {t('withdrawals.continueOnboarding', 'Continue Onboarding')}
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Exchange tokens form */}
                  {onboardingCompleted && payoutsEnabled && user.tokenBalance >= MINIMUM_WITHDRAWAL && (
                    <div className="card border-0 mb-4 mt-4">
                      <div className="card-body">
                        <h5 className="mb-3">{t('withdrawals.exchangeTokensTitle', 'Exchange Tokens for Credit')}</h5>
                        
                        <div className="alert alert-primary border-0 d-flex mb-4">
                          <i className="bi bi-info-circle-fill fs-4 me-2"></i>
                          <div>
                            <div className="fw-medium mb-1">{t('withdrawals.howItWorks', 'How It Works')}</div>
                            <div className="small">{t('withdrawals.exchangeExplanation', 'When you exchange tokens, the equivalent USD amount will be transferred to your Stripe account. From there, you can withdraw the funds to your bank account through the Stripe dashboard.')}</div>
                          </div>
                        </div>
                        
                        <div className="mb-4">
                          <label htmlFor="withdrawAmount" className="form-label fw-medium">
                            {t('withdrawals.withdrawalAmount', 'Tokens to Exchange')}
                          </label>
                          
                          {/* Quick select percentage buttons */}
                          <div className="btn-group w-100 mb-3">
                            <button
                              type="button"
                              className="btn btn-outline-secondary" 
                              onClick={() => handleSetPercentage(25)}
                            >
                              25%
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-outline-secondary" 
                              onClick={() => handleSetPercentage(50)}
                            >
                              50%
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-outline-secondary" 
                              onClick={() => handleSetPercentage(75)}
                            >
                              75%
                            </button>
                            <button 
                              type="button" 
                              className="btn btn-outline-secondary" 
                              onClick={() => handleSetPercentage(100)}
                            >
                              Max
                            </button>
                          </div>
                          
                          <div className="input-group input-group-lg mb-3">
                            <input 
                              type="text" 
                              className="form-control form-control-lg" 
                              id="withdrawAmount" 
                              value={withdrawAmount}
                              onChange={handleWithdrawAmountChange}
                              onBlur={handleInputBlur}
                            />
                            <span className="input-group-text">{t('withdrawals.tokens', 'Tokens')}</span>
                          </div>
                          
                          <div className="form-text mb-3">
                            {t('withdrawals.minimumWithdrawal', 'Minimum withdrawal: {{minimum}} tokens', { minimum: MINIMUM_WITHDRAWAL.toLocaleString() })}
                          </div>
                        </div>

                        <div className="mb-4">
                          <label htmlFor="withdrawAmountUSD" className="form-label fw-medium">
                            {t('withdrawals.withdrawalAmountUSD', 'Amount in USD')}
                          </label>
                          <div className="input-group input-group-lg">
                            <input 
                              type="text" 
                              className="form-control form-control-lg" 
                              id="withdrawAmountUSD" 
                              value={((withdrawAmount / EXCHANGE_RATE) * 100).toFixed(2)} 
                              disabled 
                            />
                            <span className="input-group-text">USD</span>
                          </div>
                          <div className="form-text">
                            {t('withdrawals.exchangeRate', 'Exchange rate: 10,000 tokens = 100 USD')}
                          </div>
                        </div>

                        <div className="d-flex gap-2">
                          <button 
                            type="button" 
                            className="btn btn-primary btn-lg flex-grow-1"
                            onClick={handleWithdraw}
                            disabled={loading || withdrawAmount < MINIMUM_WITHDRAWAL}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                {t('common.processing', 'Processing...')}
                              </>
                            ) : (
                              <>
                                <i className="bi bi-cash-coin me-2"></i>
                                {t('withdrawals.initiateWithdrawal', 'Exchange Tokens for Credit')}
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {/* Warning for incomplete verification */}
                  {stripeConnected && !onboardingCompleted && (
                    <div className="alert alert-warning border-0 mt-4">
                      <div className="d-flex">
                        <i className="bi bi-exclamation-triangle-fill fs-4 me-2"></i>
                        <div>
                          <div className="fw-medium mb-1">{t('withdrawals.completeVerification', 'Complete Your Verification')}</div>
                          <div className="small">{t('withdrawals.verificationRequired', 'To withdraw funds, you need to complete the Stripe onboarding process. This includes verifying your identity and providing bank account details.')}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm mb-4">
            <div className="card-header bg-body border-bottom-0 py-4">
              <h2 className="h4 mb-0">{t('withdrawals.info', 'How It Works')}</h2>
            </div>
            <div className="card-body">
              <div className="timeline">
                <div className="timeline-item mb-4">
                  <div className="timeline-marker bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mb-2" style={{ width: '32px', height: '32px' }}>1</div>
                  <h6 className="fw-medium mb-2">{t('withdrawals.step1', 'Connect with Stripe')}</h6>
                  <p className="text-muted small mb-0">{t('withdrawals.step1Desc', 'Link your account with Stripe to enable secure fund transfers')}</p>
                </div>
                <div className="timeline-item mb-4">
                  <div className="timeline-marker bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mb-2" style={{ width: '32px', height: '32px' }}>2</div>
                  <h6 className="fw-medium mb-2">{t('withdrawals.step2', 'Verify Identity')}</h6>
                  <p className="text-muted small mb-0">{t('withdrawals.step2Desc', 'Complete Stripe\'s identity verification process for security')}</p>
                </div>
                <div className="timeline-item mb-4">
                  <div className="timeline-marker bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mb-2" style={{ width: '32px', height: '32px' }}>3</div>
                  <h6 className="fw-medium mb-2">{t('withdrawals.step3', 'Exchange Tokens')}</h6>
                  <p className="text-muted small mb-0">{t('withdrawals.step3Desc', 'Convert your tokens into cash in your Stripe account')}</p>
                </div>
                <div className="timeline-item">
                  <div className="timeline-marker bg-primary text-white rounded-circle d-flex align-items-center justify-content-center mb-2" style={{ width: '32px', height: '32px' }}>4</div>
                  <h6 className="fw-medium mb-2">{t('withdrawals.step4', 'Withdraw Funds')}</h6>
                  <p className="text-muted small mb-0">{t('withdrawals.step4Desc', 'Transfer the cash from your Stripe account to your bank')}</p>
                </div>
              </div>
              
              <div className="alert alert-warning border-0 mt-4 mb-0">
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle-fill fs-4 me-2"></i>
                  <div>
                    <div className="fw-medium mb-1">{t('withdrawals.important', 'Important')}</div>
                    <div className="small">{t('withdrawals.kycRequired', 'Stripe requires identity verification to process withdrawals. This is for security and regulatory compliance.')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StripeWithdrawal; 